import { localizedPath } from '../i18n/paths'
import { useState, useEffect } from 'react'
import { useLocale } from '../i18n/LocaleProvider'
import { usePageTitle } from '../hooks/usePageTitle'
import { articleTelemetryDisabled, setArticleTelemetryDisabled } from '../hooks/useArticleTelemetry'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import './LegalPage.css'

export default function PrivacyPage() {
  const [analyticsDisabled, setAnalyticsDisabled] = useState(true)
  const changeAnalyticsPreference = () => {
    setArticleTelemetryDisabled(!analyticsDisabled)
    setAnalyticsDisabled(articleTelemetryDisabled())
  }
  useEffect(() => { setAnalyticsDisabled(articleTelemetryDisabled()) }, [])
  const { locale: lang } = useLocale()
  usePageTitle(lang === 'ja' ? 'プライバシーポリシー' : 'Privacy Policy')
  useEffect(() => {
    document.body.classList.add('body-legal')
    document.body.style.background = '#F2F2F7'
    return () => {
      document.body.classList.remove('body-legal')
      document.body.style.background = ''
    }
  }, [])

  useEffect(() => {
    const tocId    = lang === 'en' ? 'privacy-toc-en' : 'privacy-toc-ja'
    const tocLinks = document.querySelectorAll(`#${tocId} .lp-toc__link`)
    if (!tocLinks.length) return

    const ids      = Array.from(tocLinks).map(l => l.getAttribute('href')!.slice(1))
    const sections = ids.map(id => document.getElementById(id)).filter(Boolean) as HTMLElement[]

    let activeId: string | null = null
    const setActive = (id: string) => {
      if (id === activeId) return
      activeId = id
      tocLinks.forEach(l => l.classList.toggle('is-active', l.getAttribute('href') === '#' + id))
    }

    const observer = new IntersectionObserver(entries => {
      const visible = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
      if (visible.length) setActive(visible[0].target.id)
    }, { rootMargin: '-80px 0px -60% 0px', threshold: 0 })

    sections.forEach(s => observer.observe(s))
    if (sections[0]) setActive(sections[0].id)
    return () => observer.disconnect()
  }, [lang])

  const ja = lang === 'ja'

  return (
    <>
      <Navbar lightMode />
      <div className="legal-page">
      <div className="lp-page-spacer" aria-hidden="true" />

      <div className="lp-page-header">
        <div className="lp-page-header__inner">
          <h1 className="lp-page-header__title">{ja ? 'プライバシーポリシー' : 'Privacy Policy'}</h1>
          <div className="lp-page-header__dates">
            <span>{ja ? '施行日：2026年4月21日' : 'Effective Date: April 21, 2026'}</span>
            <span>{ja ? '最終更新日：2026年9月23日' : 'Last Updated: September 23, 2026'}</span>
          </div>
        </div>
      </div>

      <div className="lp-doc-link-row">
        <a href={localizedPath("/terms", lang)} className="lp-doc-link">{ja ? '利用規約 →' : 'Terms of Service →'}</a>
      </div>

      {/* ══════════════ ENGLISH ══════════════ */}
      {lang === 'en' && (<div className="lp-lang-section is-active" lang="en">
        <div className="lp-layout-wrap">
          <div className="lp-layout">

            <aside className="lp-toc" aria-label="Table of contents">
              <div className="lp-toc__label">Contents</div>
              <nav className="lp-toc__nav" id="privacy-toc-en">
                <a href="#p1"  className="lp-toc__link">1. Who We Are</a>
                <a href="#p2"  className="lp-toc__link">2. Scope of This Policy</a>
                <a href="#p3"  className="lp-toc__link">3. Information We Process</a>
                <a href="#p4"  className="lp-toc__link">4. How Your Content Is Processed</a>
                <a href="#p5"  className="lp-toc__link">5. Legal Basis for Processing</a>
                <a href="#p6"  className="lp-toc__link">6. How We Use Information</a>
                <a href="#p7"  className="lp-toc__link">7. Data Sharing and Disclosure</a>
                <a href="#p8"  className="lp-toc__link">8. Data Retention</a>
                <a href="#p9"  className="lp-toc__link">9. Data Security</a>
                <a href="#p10" className="lp-toc__link">10. Your Privacy Rights</a>
                <a href="#p11" className="lp-toc__link">11. Children's Privacy</a>
                <a href="#p12" className="lp-toc__link">12. International Data Transfers</a>
                <a href="#p13" className="lp-toc__link">13. Third-Party Links</a>
                <a href="#p14" className="lp-toc__link">14. Do Not Track</a>
                <a href="#p15" className="lp-toc__link">15. Apple App Store and iOS</a>
                <a href="#p16" className="lp-toc__link">16. Changes to This Policy</a>
                <a href="#p17" className="lp-toc__link">17. Contact Us</a>
              </nav>
            </aside>

            <main className="lp-policy-card" id="privacy-content-en">

              <section className="lp-section" id="p1">
                <div className="lp-section-head"><span className="lp-section-num">1</span><h2>Who We Are</h2></div>
                <p>Bybeit is developed and operated by Bybeit, Inc., a corporation registered in Japan. We build AI-powered productivity and communication tools, including the Cueply iOS keyboard extension.</p>
                <div className="lp-contact">
                  <p><strong>Bybeit, Inc.</strong></p>
                  <p>Email: <a href="mailto:privacy@bybeit.app">privacy@bybeit.app</a></p>
                  <p>Website: <a href="https://bybeit.app" target="_blank" rel="noopener">https://bybeit.app</a></p>
                </div>
              </section>

              <section className="lp-section" id="p2">
                <div className="lp-section-head"><span className="lp-section-num">2</span><h2>Scope of This Policy</h2></div>
                <p>This Privacy Policy ("Policy") applies to information processed through your use of the Bybeit website and mobile applications, including the Cueply iOS keyboard extension. It does not apply to:</p>
                <ul>
                  <li>Third-party websites, services, or applications you may access through or alongside our Services;</li>
                  <li>Apple's privacy practices in connection with the App Store or iOS operating system;</li>
                  <li>The privacy practices of third-party AI providers used through our applications, which are governed by their own separate privacy policies.</li>
                </ul>
              </section>

              <section className="lp-section" id="p3">
                <div className="lp-section-head"><span className="lp-section-num">3</span><h2>Information We Process</h2></div>
                <h3>3.1 Information You Voluntarily Provide</h3>
                <p>Bybeit applications are designed as privacy-first products. The only data processed through our apps is screenshots or images you explicitly and voluntarily import for AI analysis ("User Content").</p>
                <p>We do not require account creation for core functionality. We do not ask for, collect, or store your name, email address, phone number, location, or any other personal identifiers.</p>
                <h3>3.2 Information We Do Not Collect</h3>
                <p>In our mobile applications, we do not do the following. Website article analytics is described separately in section 3.4:</p>
                <ul>
                  <li>We do not collect, store, or retain any screenshots or images you import on our servers;</li>
                  <li>We do not build user profiles or maintain any persistent records about you;</li>
                  <li>We do not collect device identifiers, advertising identifiers (IDFA), or persistent unique IDs;</li>
                  <li>We do not access your contacts, camera, microphone, location, or other device resources beyond what is minimally necessary to operate the application;</li>
                  <li>We do not use cookies, web beacons, or similar tracking technologies;</li>
                  <li>We do not collect behavioral analytics or usage data on our own infrastructure.</li>
                </ul>
                <h3>3.3 Automatically Collected Technical Information</h3>
                <p>Standard iOS crash reports and diagnostic data may be made available to us through Apple's App Store Connect platform, subject to your iOS device settings and Apple's privacy policy. Such data does not include the content of your screenshots and is governed by Apple's terms.</p>
                <h3>3.4 Website Article Analytics</h3>
                <p>On published Blog and Newsroom articles, we measure page views, active reading time, how far the article has been displayed, referring domains, campaign source and medium, broad device and browser categories, and browser language. We use these measurements to understand and improve our editorial content. They do not include form contents, full referring URLs, or URL query strings other than limited campaign labels.</p>
                <p>A random session ID is stored in the current browser tab's session storage and renewed after 30 minutes of inactivity on a subsequent article visit. It is not an advertising ID or a cross-site identifier. Our analytics records do not store IP addresses, account identities, or a full user-agent string. Firebase provides the hosting and database infrastructure and may process technical request logs separately.</p>
                <p>Event and session records are scheduled to expire after 2 days; daily aggregate reports after 400 days. Automatic deletion may occur later than the expiry time. Article lifetime totals are retained. These website analytics retention periods are separate from the mobile-app diagnostic periods below.</p>
                <p>We honor browser Do Not Track and Global Privacy Control signals. You can also disable article analytics for this browser below. Enabling analytics takes effect on a subsequent article visit. This preference is saved locally; clearing browser storage resets it.</p>
                <button type="button" onClick={changeAnalyticsPreference} aria-pressed={analyticsDisabled}>{analyticsDisabled ? 'Article analytics: off — enable' : 'Article analytics: on — disable'}</button>
                <p>When your browser sends Do Not Track or Global Privacy Control, analytics remains off regardless of this setting.</p>
              </section>

              <section className="lp-section" id="p4">
                <div className="lp-section-head"><span className="lp-section-num">4</span><h2>How Your Content Is Processed — Third-Party AI Providers</h2></div>
                <h3>4.1 Transmission to AI Providers</h3>
                <p>The core functionality of Bybeit applications requires transmitting your screenshot to a third-party AI service for analysis and reply generation. You may select which AI provider processes your content:</p>
                <ul>
                  <li>OpenAI, LLC ("OpenAI") — using OpenAI's API services;</li>
                  <li>Anthropic, PBC ("Anthropic") — using Anthropic's API services.</li>
                </ul>
                <p>Your screenshot is transmitted directly from your device to the selected provider's API endpoint over an encrypted HTTPS connection. Bybeit does not receive, intercept, or store a copy of your screenshot at any point during this transmission.</p>
                <h3>4.2 Third-Party Provider Data Practices</h3>
                <div className="lp-callout lp-callout-important">
                  <div className="lp-callout-label">Important Notice</div>
                  <p>We do not control, and cannot guarantee, how OpenAI or Anthropic handle, store, use, or retain the data you transmit to them. Each provider's handling of your data is governed solely by their respective terms and privacy policies. We encourage you to review these carefully:</p>
                </div>
                <ul className="lp-providers">
                  <li><a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener">OpenAI Privacy Policy</a></li>
                  <li><a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener">Anthropic Privacy Policy</a></li>
                </ul>
                <p>As of the date of this Policy, we understand that both OpenAI and Anthropic represent that API inputs may be used for safety and policy compliance purposes. However, their policies are subject to change, and Bybeit makes no warranties regarding those policies.</p>
                <h3>4.3 Your Responsibility Regarding Content</h3>
                <p>Because your screenshot is transmitted to a third-party AI provider, you should:</p>
                <ul>
                  <li>Avoid importing screenshots that contain sensitive personal information (such as financial data, government-issued ID numbers, health information, passwords, or private communications you do not intend to share with a third-party AI service);</li>
                  <li>Ensure you have the right to share the content of any screenshot you import, including with respect to the privacy rights of any third parties depicted in that screenshot.</li>
                </ul>
              </section>

              <section className="lp-section" id="p5">
                <div className="lp-section-head"><span className="lp-section-num">5</span><h2>Legal Basis for Processing (Where Applicable)</h2></div>
                <p>Where applicable law requires a legal basis for processing personal information, we rely on the following:</p>
                <ul>
                  <li><strong>Performance of a Contract / Service:</strong> Processing User Content is necessary to provide the application's core functionality at your request.</li>
                  <li><strong>Legitimate Interests:</strong> Processing aggregate crash reports to maintain and improve application stability.</li>
                  <li><strong>Consent:</strong> Where required, we will seek your consent before processing any information not described in this Policy.</li>
                </ul>
                <p>For users in the EEA, United Kingdom, or other GDPR-equivalent jurisdictions, the transmission of your screenshot to a third-party AI provider is initiated by you at your direction and constitutes processing under your explicit instruction.</p>
              </section>

              <section className="lp-section" id="p6">
                <div className="lp-section-head"><span className="lp-section-num">6</span><h2>How We Use Information</h2></div>
                <p>To the limited extent we receive any information (e.g., crash diagnostic data from Apple), we use it solely for:</p>
                <ul>
                  <li>Operating, maintaining, and improving the technical performance and stability of our applications;</li>
                  <li>Investigating and resolving technical issues or bugs;</li>
                  <li>Complying with applicable legal obligations;</li>
                  <li>Enforcing our Terms of Service and protecting the rights of Bybeit and our users.</li>
                </ul>
                <p>We do not use your information for advertising, marketing, behavioral profiling, or any purpose beyond those listed above.</p>
              </section>

              <section className="lp-section" id="p7">
                <div className="lp-section-head"><span className="lp-section-num">7</span><h2>Data Sharing and Disclosure</h2></div>
                <h3>7.1 Third-Party AI Providers</h3>
                <p>As described in Section 4, your screenshot is transmitted to your selected AI provider (OpenAI or Anthropic). This is the only sharing of User Content that occurs, and it is initiated by you at your express direction.</p>
                <h3>7.2 Service Providers</h3>
                <p>We may engage third-party vendors providing infrastructure, security, or analytics services to support operation of our applications. Any such vendors are bound by confidentiality obligations and prohibited from using information for their own purposes.</p>
                <h3>7.3 Legal Requirements and Safety</h3>
                <p>We may disclose information if we believe in good faith that disclosure is reasonably necessary to: comply with a legal obligation or enforceable governmental request; enforce our Terms of Service; protect the safety, rights, or property of Bybeit, our users, or the public; or detect, prevent, or address fraud, security, or technical issues.</p>
                <h3>7.4 Business Transfers</h3>
                <p>In the event of a merger, acquisition, reorganization, or sale of assets, information held by us may be transferred as part of that transaction. We will provide notice if such a transfer results in a material change to this Policy.</p>
                <h3>7.5 No Sale of Personal Information</h3>
                <p>We do not sell, rent, or trade personal information to third parties for their own commercial purposes.</p>
              </section>

              <section className="lp-section" id="p8">
                <div className="lp-section-head"><span className="lp-section-num">8</span><h2>Data Retention</h2></div>
                <p>Bybeit retains no copies of screenshots or images you import on our systems. User Content is transmitted ephemerally to the selected AI provider and is not stored by Bybeit.</p>
                <p>Any incidental technical data (such as crash logs accessible through Apple's App Store Connect) is retained only for as long as necessary to resolve the relevant issue, and in any event for no longer than twelve (12) months.</p>
                <p>Retention of your data by OpenAI or Anthropic is governed by their respective privacy policies and retention schedules.</p>
              </section>

              <section className="lp-section" id="p9">
                <div className="lp-section-head"><span className="lp-section-num">9</span><h2>Data Security</h2></div>
                <p>We implement reasonable technical and organizational measures to protect information against unauthorized access, alteration, disclosure, or destruction. These measures include:</p>
                <ul>
                  <li>Encrypted transmission of User Content via HTTPS/TLS to third-party AI provider endpoints;</li>
                  <li>No server-side storage of User Content by Bybeit;</li>
                  <li>Limiting access to technical systems to authorized personnel only.</li>
                </ul>
                <p>However, no method of electronic transmission or storage is completely secure. We cannot guarantee absolute security and encourage you to exercise caution regarding the content of screenshots you choose to import.</p>
              </section>

              <section className="lp-section" id="p10">
                <div className="lp-section-head"><span className="lp-section-num">10</span><h2>Your Privacy Rights</h2></div>
                <h3>10.1 General Rights</h3>
                <p>Depending on your jurisdiction, you may have the following rights with respect to your personal information:</p>
                <ul className="lp-rights">
                  <li><strong>Access</strong><span>The right to request information about personal data we hold about you.</span></li>
                  <li><strong>Correction</strong><span>The right to request correction of inaccurate personal data.</span></li>
                  <li><strong>Deletion</strong><span>The right to request deletion of your personal data.</span></li>
                  <li><strong>Portability</strong><span>The right to receive your personal data in a portable format.</span></li>
                  <li><strong>Objection</strong><span>The right to object to or request restriction of certain processing activities.</span></li>
                  <li><strong>Withdrawal</strong><span>Where processing is based on consent, the right to withdraw consent at any time.</span></li>
                </ul>
                <p>Because Bybeit does not collect or retain personal data from users in the ordinary course of operating the application, many of these rights may have limited practical application to us specifically. However, you may exercise rights with respect to data held by OpenAI or Anthropic by contacting those providers directly.</p>
                <h3>10.2 California Residents — CCPA / CPRA</h3>
                <p>If you are a California resident, you may have rights under the CCPA, as amended by the CPRA, including the right to know, delete, and opt out of the sale or sharing of personal information. Bybeit does not sell or share personal information. To submit a privacy request, contact us at <a href="mailto:privacy@bybeit.app">privacy@bybeit.app</a>.</p>
                <h3>10.3 EEA / UK Residents — GDPR</h3>
                <p>If you are located in the EEA or United Kingdom, you have rights under GDPR or UK GDPR, including rights of access, rectification, erasure, restriction, portability, and the right to lodge a complaint with a supervisory authority. To exercise your rights, contact us at <a href="mailto:privacy@bybeit.app">privacy@bybeit.app</a>.</p>
                <h3>10.4 Japan Residents — APPI</h3>
                <p>If you are located in Japan, your personal information is protected under the Act on the Protection of Personal Information (APPI) and applicable PPC guidelines. You have the right to request disclosure, correction, deletion, cessation of use, and cessation of provision to third parties. Contact us at <a href="mailto:privacy@bybeit.app">privacy@bybeit.app</a>.</p>
                <h3>10.5 How to Submit a Request</h3>
                <p>To exercise any privacy rights, contact us at <a href="mailto:privacy@bybeit.app">privacy@bybeit.app</a>. We will respond within the timeframe required by applicable law (generally thirty (30) days). We may need to verify your identity before processing your request.</p>
              </section>

              <section className="lp-section" id="p11">
                <div className="lp-section-head"><span className="lp-section-num">11</span><h2>Children's Privacy</h2></div>
                <p>Our Services are not directed to children under the age of 13 (or 16 in certain EEA member states), and we do not knowingly collect personal information from such children. If you believe a child has provided personal information through our Services, please contact us at <a href="mailto:privacy@bybeit.app">privacy@bybeit.app</a> and we will take steps to delete such information.</p>
                <p>Our applications are intended for users who are 17 years of age or older, consistent with their App Store age rating. Use by minors is subject to parental or guardian consent where required by law.</p>
              </section>

              <section className="lp-section" id="p12">
                <div className="lp-section-head"><span className="lp-section-num">12</span><h2>International Data Transfers</h2></div>
                <p>Bybeit is based in Japan. By using our Services, you acknowledge that your User Content will be transmitted to the AI provider's servers, which may be located outside your country of residence, including in the United States.</p>
                <p>Where such transfers involve personal data from the EEA or UK, OpenAI and Anthropic have represented that they maintain appropriate safeguards (such as Standard Contractual Clauses) for such transfers. Please review their respective privacy policies for details.</p>
              </section>

              <section className="lp-section" id="p13">
                <div className="lp-section-head"><span className="lp-section-num">13</span><h2>Third-Party Links and Services</h2></div>
                <p>Our Services may contain links to third-party websites or services, including the privacy policy links of our AI providers. These third-party sites have their own privacy policies, and we have no responsibility or liability for their content or practices. We encourage you to review the privacy policies of any third-party services you access.</p>
              </section>

              <section className="lp-section" id="p14">
                <div className="lp-section-head"><span className="lp-section-num">14</span><h2>Do Not Track</h2></div>
                <p>Our Services do not track users across third-party apps or websites for advertising purposes. Website article analytics is disabled when the browser sends Do Not Track or Global Privacy Control, as described in section 3.4.</p>
              </section>

              <section className="lp-section" id="p15">
                <div className="lp-section-head"><span className="lp-section-num">15</span><h2>Apple App Store and iOS Platform</h2></div>
                <p>Our applications are distributed through the Apple App Store. Apple's collection and use of information related to your use of the App Store is governed by Apple's Privacy Policy, available at <a href="https://www.apple.com/legal/privacy" target="_blank" rel="noopener">https://www.apple.com/legal/privacy</a>. Bybeit is not responsible for Apple's data practices.</p>
              </section>

              <section className="lp-section" id="p16">
                <div className="lp-section-head"><span className="lp-section-num">16</span><h2>Changes to This Privacy Policy</h2></div>
                <p>We may update this Policy from time to time. When we do, we will revise the "Last Updated" date at the top of this document. For material changes, we will provide notice through the application or through other reasonable means, as required by applicable law.</p>
                <p>Your continued use of our Services after the effective date of a revised Policy constitutes your acceptance of the updated Policy. We encourage you to review this Policy periodically.</p>
              </section>

              <section className="lp-section" id="p17">
                <div className="lp-section-head"><span className="lp-section-num">17</span><h2>Contact Us</h2></div>
                <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:</p>
                <div className="lp-contact">
                  <p><strong>Bybeit, Inc.</strong></p>
                  <p>Email: <a href="mailto:privacy@bybeit.app">privacy@bybeit.app</a></p>
                  <p>Website: <a href="https://bybeit.app" target="_blank" rel="noopener">https://bybeit.app</a></p>
                </div>
                <p>We will make reasonable efforts to respond to all inquiries within thirty (30) days, or within the timeframe required by applicable law.</p>
              </section>

              <div className="lp-footer-note">
                <p>&copy; 2026 Bybeit, Inc. All rights reserved.</p>
                <p>Bybeit &mdash; Privacy Policy &mdash; Effective April 21, 2026</p>
              </div>

            </main>
          </div>
        </div>
      </div>)}

      {/* ══════════════ JAPANESE ══════════════ */}
      {lang === 'ja' && (<div className="lp-lang-section is-active" lang="ja">
        <div className="lp-layout-wrap">
          <div className="lp-layout">

            <aside className="lp-toc" aria-label="目次">
              <div className="lp-toc__label">目次</div>
              <nav className="lp-toc__nav" id="privacy-toc-ja">
                <a href="#pp1"  className="lp-toc__link">第1条　事業者情報</a>
                <a href="#pp2"  className="lp-toc__link">第2条　適用範囲</a>
                <a href="#pp3"  className="lp-toc__link">第3条　取り扱う情報</a>
                <a href="#pp4"  className="lp-toc__link">第4条　コンテンツの処理方法</a>
                <a href="#pp5"  className="lp-toc__link">第5条　個人情報保護法に基づく取り扱い</a>
                <a href="#pp6"  className="lp-toc__link">第6条　保存期間</a>
                <a href="#pp7"  className="lp-toc__link">第7条　データセキュリティ</a>
                <a href="#pp8"  className="lp-toc__link">第8条　お客様の権利</a>
                <a href="#pp9"  className="lp-toc__link">第9条　未成年者の個人情報</a>
                <a href="#pp10" className="lp-toc__link">第10条　国際的なデータ移転</a>
                <a href="#pp11" className="lp-toc__link">第11条　第三者サービスへのリンク</a>
                <a href="#pp12" className="lp-toc__link">第12条　Appleプラットフォーム</a>
                <a href="#pp13" className="lp-toc__link">第13条　本ポリシーの変更</a>
                <a href="#pp14" className="lp-toc__link">第14条　準拠法および管轄</a>
                <a href="#pp15" className="lp-toc__link">第15条　お問い合わせ窓口</a>
              </nav>
            </aside>

            <main className="lp-policy-card ja" id="privacy-content-ja" lang="ja">

              <section className="lp-section" id="pp1">
                <div className="lp-section-head"><span className="lp-section-num">1</span><h2>第1条（事業者情報）</h2></div>
                <p>バイバイトは、日本法に基づき設立された法人であるバイバイト株式会社（以下「当社」といいます。）が開発・運営するサービスです。当社は、AIを活用した生産性・コミュニケーションツール（iOSキーボード拡張アプリケーション「Cueboard」を含みます。）を提供しています。当社の連絡先は以下のとおりです。</p>
                <div className="lp-contact">
                  <p><strong>バイバイト株式会社</strong></p>
                  <p>個人情報お問い合わせ窓口</p>
                  <p>メールアドレス：<a href="mailto:privacy@bybeit.app">privacy@bybeit.app</a></p>
                  <p>ウェブサイト：<a href="https://bybeit.app" target="_blank" rel="noopener">https://bybeit.app</a></p>
                </div>
              </section>

              <section className="lp-section" id="pp2">
                <div className="lp-section-head"><span className="lp-section-num">2</span><h2>第2条（適用範囲）</h2></div>
                <p>本プライバシーポリシー（以下「本ポリシー」といいます。）は、お客様がバイバイトのウェブサイトおよびモバイルアプリケーション（Cueboardを含みます。）を通じて行う情報処理に適用されます。</p>
                <p>本ポリシーは、以下には適用されません。</p>
                <ul>
                  <li>本サービスを通じてまたは本サービスと併せてアクセスする第三者のウェブサイト、サービスまたはアプリケーション</li>
                  <li>App StoreおよびiOSオペレーティングシステムに関連するApple Inc.のプライバシー慣行</li>
                  <li>本アプリが利用する第三者AIプロバイダーのプライバシー慣行（当該プロバイダー各自のプライバシーポリシーに従います。）</li>
                </ul>
              </section>

              <section className="lp-section" id="pp3">
                <div className="lp-section-head"><span className="lp-section-num">3</span><h2>第3条（取り扱う情報）</h2></div>
                <h3>第3条第1項（お客様が任意に提供する情報）</h3>
                <p>バイバイトのアプリケーションはプライバシーを最優先に設計されています。本アプリを通じて処理される情報は、お客様が分析目的で本アプリに明示的かつ任意にインポートするスクリーンショットまたは画像（以下「ユーザーコンテンツ」といいます。）に限られます。</p>
                <p>当社は、アカウントの作成を要求しません。氏名、メールアドレス、電話番号、位置情報その他の個人を識別する情報の取得、収集または保存は行いません。</p>
                <h3>第3条第2項（収集しない情報）</h3>
                <p>以下はモバイルアプリに関する説明です。ウェブサイトの記事分析については第3条第4項をご覧ください。</p>
                <p>当社が行わない事項を以下に明示します。</p>
                <ul>
                  <li>お客様がインポートしたスクリーンショットまたは画像を当社のサーバーで収集、保存または保持すること</li>
                  <li>ユーザープロファイルの構築またはお客様に関する継続的な記録の保持</li>
                  <li>端末識別子、広告識別子（IDFA）または持続的な固有IDの収集</li>
                  <li>アプリケーションの動作に最低限必要な範囲を超えて、連絡先、カメラ、マイク、位置情報その他の端末リソースへのアクセス</li>
                  <li>Cookie、ウェブビーコンその他の類似するトラッキング技術の使用</li>
                  <li>当社独自のインフラストラクチャにおける行動分析または利用状況データの収集</li>
                </ul>
                <h3>第3条第3項（自動的に収集される技術情報）</h3>
                <p>iOSオペレーティングシステムの標準的なクラッシュレポートおよび診断データは、お客様のiOS端末設定およびAppleのプライバシーポリシーに従い、Apple Inc.のApp Store Connectプラットフォームを通じて当社に提供される場合があります。当該データはAppleの規約に従い取り扱われ、ユーザーコンテンツの内容を含みません。</p>
                <h3>第3条第4項（ウェブサイトの記事分析）</h3>
                <p>公開されたBlog・Newsroomの記事について、閲覧数、アクティブな閲覧時間、本文の表示到達位置、参照元ドメイン、キャンペーンの流入元・媒体、端末・ブラウザの大まかな種類、ブラウザの言語を計測し、記事の改善に利用します。フォームの入力内容、参照元の完全なURL、限定されたキャンペーンラベル以外のURLクエリ文字列は分析記録に含みません。</p>
                <p>ランダムなセッションIDをブラウザのタブ内のセッションストレージに保存します。30分間操作がなければ、次の記事閲覧時に新しいIDになります。広告IDやサイトを横断する識別子ではありません。当社の分析記録にはIPアドレス、アカウント情報、完全なユーザーエージェント文字列を保存しません。ホスティングとデータベースにはFirebaseを使用し、インフラの技術的なリクエストログは別途処理される場合があります。</p>
                <p>イベント・セッション記録は2日後、日別集計は400日後に期限切れとなるよう設定します。自動削除は期限より遅れる場合があります。記事の累計閲覧数は保持します。これらは、以下のモバイルアプリの診断情報とは別の保存期間です。</p>
                <p>ブラウザのDo Not TrackおよびGlobal Privacy Controlに従います。下の設定でも、このブラウザでの記事分析を無効にできます。有効化は次の記事閲覧から反映されます。設定はブラウザ内に保存され、ストレージを消去するとリセットされます。</p>
                <button type="button" onClick={changeAnalyticsPreference} aria-pressed={analyticsDisabled}>{analyticsDisabled ? '記事分析：無効 — 有効にする' : '記事分析：有効 — 無効にする'}</button>
                <p>Do Not TrackまたはGlobal Privacy Controlが有効な場合、この設定にかかわらず記事分析は無効です。</p>
              </section>

              <section className="lp-section" id="pp4">
                <div className="lp-section-head"><span className="lp-section-num">4</span><h2>第4条（コンテンツの処理方法—第三者AIプロバイダー）</h2></div>
                <h3>第4条第1項（AIプロバイダーへの送信）</h3>
                <p>本アプリの中核機能として、お客様がインポートしたスクリーンショットを分析および返信候補生成のために第三者AIサービスに送信する必要があります。お客様は以下のAIプロバイダーのいずれかを選択することができます。</p>
                <ul>
                  <li>OpenAI, LLC（以下「OpenAI」といいます。）―OpenAIのAPIサービスを利用</li>
                  <li>Anthropic, PBC（以下「Anthropic」といいます。）―AnthropicのAPIサービスを利用</li>
                </ul>
                <p>お客様のスクリーンショットは、暗号化されたHTTPS通信により、お客様の端末から選択されたプロバイダーのAPIエンドポイントへ直接送信されます。当社は、この送信プロセスにおいて、お客様のスクリーンショットのコピーを受信、傍受または保存しません。</p>
                <h3>第4条第2項（第三者プロバイダーのデータ取り扱い）</h3>
                <div className="lp-callout lp-callout-important">
                  <div className="lp-callout-label">重要事項</div>
                  <p>当社は、OpenAIまたはAnthropicがお客様から送信されたデータをいかに取り扱い、保存し、使用し、または保持するかについて、管理または保証する立場にありません。各プロバイダーによるデータの取り扱いは、それぞれのサービス利用規約およびプライバシーポリシーにのみ従います。お客様は以下をご参照ください。</p>
                </div>
                <ul className="lp-providers">
                  <li><a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener">OpenAI プライバシーポリシー</a></li>
                  <li><a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener">Anthropic プライバシーポリシー</a></li>
                </ul>
                <h3>第4条第3項（コンテンツに関するお客様の責任）</h3>
                <p>お客様のスクリーンショットは第三者AIプロバイダーに送信されますため、お客様は以下の点にご留意ください。</p>
                <ul>
                  <li>第三者AIサービスへの共有を意図しない機微な個人情報（金融データ、マイナンバーその他の公的識別番号、健康・医療情報、パスワード、個人的な通信内容等）を含むスクリーンショットのインポートはお避けください。</li>
                  <li>インポートするスクリーンショットの内容を共有する権限をお客様が有していることをご確認ください。当該スクリーンショットに描写または言及される第三者のプライバシー権を含みます。</li>
                </ul>
              </section>

              <section className="lp-section" id="pp5">
                <div className="lp-section-head"><span className="lp-section-num">5</span><h2>第5条（個人情報保護法に基づく取り扱い）</h2></div>
                <p>当社は、「個人情報の保護に関する法律」（以下「個人情報保護法」といいます。）に定める個人情報取扱事業者として、同法および個人情報保護委員会が定めるガイドライン・告示を遵守し、個人情報を適切に取り扱います。</p>
                <h3>第5条第1項（利用目的）</h3>
                <p>当社が個人情報を取得した場合の利用目的は以下のとおりです。</p>
                <ul>
                  <li>本アプリの技術的な動作、安定性の維持および改善</li>
                  <li>技術的な問題またはバグの調査および解決</li>
                  <li>法令上の義務の履行</li>
                  <li>当社の利用規約の執行ならびに当社およびお客様の権利の保護</li>
                </ul>
                <p>当社は、上記の目的以外に個人情報を利用しません。広告、マーケティング、行動プロファイリングその他上記以外の目的には使用しません。</p>
                <h3>第5条第2項（第三者提供）</h3>
                <p>当社は、個人情報保護法第27条に従い、あらかじめご本人の同意を得ることなく個人情報を第三者に提供しません。ただし、法令に基づく場合、人の生命・身体または財産の保護のために必要がある場合であってご本人の同意を得ることが困難な場合等はこの限りではありません。</p>
                <h3>第5条第3項（安全管理措置）</h3>
                <p>当社は、個人情報保護法第23条に従い、取り扱う個人情報の漏えい、滅失またはき損の防止その他の個人情報の安全管理のために必要かつ適切な措置（組織的・人的・物理的・技術的安全管理措置）を講じます。</p>
                <h3>第5条第4項（広告目的での利用禁止）</h3>
                <p>当社は、いかなる情報も広告、行動プロファイリングまたはマーケティングを目的として使用しません。当社は、商業的目的のために個人情報を第三者に販売、貸与または提供しません。</p>
              </section>

              <section className="lp-section" id="pp6">
                <div className="lp-section-head"><span className="lp-section-num">6</span><h2>第6条（保存期間）</h2></div>
                <p>当社は、お客様がインポートしたスクリーンショットまたは画像のコピーを当社のシステムに保持しません。ユーザーコンテンツは、選択されたAIプロバイダーへの一時的な送信のみに使用され、当社において保存されません。</p>
                <p>偶発的に取得された技術データ（Apple App Store Connectを通じてアクセス可能なクラッシュログ等）は、当該事象の解決に必要な期間に限り保持し、いかなる場合も取得後12か月を超えて保持しません。</p>
                <p>OpenAIまたはAnthropicによるデータの保持については、各プロバイダーのプライバシーポリシーおよび保持スケジュールに従います。</p>
              </section>

              <section className="lp-section" id="pp7">
                <div className="lp-section-head"><span className="lp-section-num">7</span><h2>第7条（データセキュリティ）</h2></div>
                <p>当社は、保有する情報を不正アクセス、改ざん、開示または破壊から保護するために、合理的な技術的・組織的措置を実施しています。具体的な措置は以下のとおりです。</p>
                <ul>
                  <li>HTTPS/TLSによるユーザーコンテンツの暗号化送信</li>
                  <li>当社によるユーザーコンテンツのサーバーサイド保存の不実施</li>
                  <li>技術システムへのアクセスを権限を有する担当者のみに限定</li>
                </ul>
                <p>ただし、電子的な送信または保存の方法に完全な安全性はありません。当社は絶対的な安全性を保証することができませんため、お客様にはインポートするスクリーンショットの内容について慎重にご判断いただくようお願いいたします。</p>
              </section>

              <section className="lp-section" id="pp8">
                <div className="lp-section-head"><span className="lp-section-num">8</span><h2>第8条（お客様の権利）</h2></div>
                <h3>第8条第1項（個人情報保護法に基づく権利）</h3>
                <p>お客様は、個人情報保護法に基づき、当社が保有するご自身に関する個人情報について以下の請求を行う権利を有します。</p>
                <ul>
                  <li>開示の請求（同法第33条）</li>
                  <li>訂正・追加・削除の請求（同法第34条）</li>
                  <li>利用停止・消去の請求（同法第35条第1項）</li>
                  <li>第三者提供の停止の請求（同法第35条第3項）</li>
                  <li>通知の請求（同法第36条）</li>
                </ul>
                <p>当社は、本アプリの通常の運用においてお客様の個人情報を収集・保持しませんため、上記の権利の多くは実際には限定的な適用にとどまる場合があります。OpenAIまたはAnthropicが保有するデータに関する権利については、各プロバイダーに直接請求することができます。</p>
                <h3>第8条第2項（請求の手続き）</h3>
                <p>上記の権利を行使される場合は、<a href="mailto:privacy@bybeit.app">privacy@bybeit.app</a> までご連絡ください。当社は、ご本人確認を行った上で、法令が定める期間内（原則として30日以内）に対応いたします。</p>
                <h3>第8条第3項（苦情の申し出）</h3>
                <p>本ポリシーに関するご不満については <a href="mailto:privacy@bybeit.app">privacy@bybeit.app</a> にお申し出ください。また、当社の対応にご不満がある場合には、個人情報保護委員会（<a href="https://www.ppc.go.jp" target="_blank" rel="noopener">https://www.ppc.go.jp</a>）に申し立てを行うことができます。</p>
              </section>

              <section className="lp-section" id="pp9">
                <div className="lp-section-head"><span className="lp-section-num">9</span><h2>第9条（未成年者の個人情報）</h2></div>
                <p>本サービスは、13歳未満の児童（一部EEA加盟国においては16歳未満）を対象とせず、当社は当該年齢未満の方から意図的に個人情報を収集しません。13歳未満の児童が個人情報を提供したと思われる場合には、<a href="mailto:privacy@bybeit.app">privacy@bybeit.app</a> までご連絡ください。当社は速やかに削除のための措置を講じます。</p>
                <p>本アプリはApp Storeの年齢区分に従い17歳以上のお客様を対象としています。未成年者による本アプリのご利用については、法令が要求する場合、保護者の同意を必要とします。</p>
              </section>

              <section className="lp-section" id="pp10">
                <div className="lp-section-head"><span className="lp-section-num">10</span><h2>第10条（国際的なデータ移転）</h2></div>
                <p>当社は日本に拠点を置いています。本サービスをご利用いただくことにより、お客様のユーザーコンテンツが、お客様の居住国外（米国を含みます。）に所在するAIプロバイダーのサーバーに送信される場合があることをご承認いただいたものとみなします。</p>
                <p>EEAまたは英国を原産地とする個人データが関与する移転については、OpenAIおよびAnthropicはいずれも当該移転について適切な保護措置（標準契約条項等）を維持していることを表明しています。詳細については各プロバイダーのプライバシーポリシーをご参照ください。</p>
              </section>

              <section className="lp-section" id="pp11">
                <div className="lp-section-head"><span className="lp-section-num">11</span><h2>第11条（第三者サービスへのリンク）</h2></div>
                <p>本サービスは、第三者のウェブサイトまたはサービス（AIプロバイダーのプライバシーポリシーへのリンクを含みます。）へのリンクを含む場合があります。当該第三者サイトは独自のプライバシーポリシーを有しており、当社はその内容または慣行について責任を負いません。お客様がアクセスされる第三者サービスのプライバシーポリシーをご確認いただくことを推奨します。</p>
              </section>

              <section className="lp-section" id="pp12">
                <div className="lp-section-head"><span className="lp-section-num">12</span><h2>第12条（Appleプラットフォームについて）</h2></div>
                <p>本アプリはApple App Storeを通じて配布されています。App Storeのご利用に関連するApple Inc.の情報収集および利用については、<a href="https://www.apple.com/legal/privacy" target="_blank" rel="noopener">https://www.apple.com/legal/privacy</a> に掲載されているAppleのプライバシーポリシーに従います。当社はAppleのデータ慣行について責任を負いません。</p>
              </section>

              <section className="lp-section" id="pp13">
                <div className="lp-section-head"><span className="lp-section-num">13</span><h2>第13条（本ポリシーの変更）</h2></div>
                <p>当社は、本ポリシーを適宜改定することがあります。改定を行う場合には、本書上部の「最終更新日」を修正します。重要な変更については、法令が要求する場合に応じ、本アプリ内またはその他の合理的な方法によりお知らせします。</p>
                <p>改定後のポリシーの施行日以降に本サービスを継続してご利用いただくことにより、お客様は改定後のポリシーに同意されたものとみなされます。定期的に本ポリシーをご確認いただくことを推奨します。</p>
              </section>

              <section className="lp-section" id="pp14">
                <div className="lp-section-head"><span className="lp-section-num">14</span><h2>第14条（準拠法および管轄）</h2></div>
                <p>本ポリシーは日本法に準拠し、日本法に従い解釈されます。本ポリシーに関連する一切の紛争については、東京地方裁判所を第一審の専属的合意管轄裁判所とします。</p>
              </section>

              <section className="lp-section" id="pp15">
                <div className="lp-section-head"><span className="lp-section-num">15</span><h2>第15条（お問い合わせ窓口）</h2></div>
                <p>本ポリシーまたは当社のプライバシー慣行に関するご質問、ご意見またはご請求については、以下の窓口までお問い合わせください。</p>
                <div className="lp-contact">
                  <p><strong>バイバイト株式会社</strong></p>
                  <p>個人情報お問い合わせ窓口</p>
                  <p>メールアドレス：<a href="mailto:privacy@bybeit.app">privacy@bybeit.app</a></p>
                  <p>ウェブサイト：<a href="https://bybeit.app" target="_blank" rel="noopener">https://bybeit.app</a></p>
                </div>
                <p>当社は、すべてのお問い合わせに対し、30日以内または法令が定める期間内に誠実に対応するよう努めます。</p>
              </section>

              <div className="lp-footer-note">
                <p>&copy; 2026 バイバイト株式会社 All rights reserved.</p>
                <p>バイバイト — プライバシーポリシー — 施行日：2026年4月21日</p>
              </div>

            </main>
          </div>
        </div>
      </div>)}
    </div>
    <Footer />
    </>
  )
}
